<template>
  <DocumentsAISummaryChatMessage
    v-if="shouldDisplaySummaryMessage"
    :document-summary="requestedSummary!"
    with-text-animation
  />
  <DocumentsAISummaryMessageFromAI
    v-else
    :message-date="requestDate"
    :isSummaryRequestPending="isSummaryRequestPending"
    :shouldDisplayErrorMessage="shouldDisplayErrorMessage"
  >
    <template v-if="shouldDisplayErrorMessage">
      <span class="font-weight-bold">
        {{ errorMessage }}
      </span>
      - {{ $t(`${TRANSLATION_KEY_PREFIX}.errors.noCredits`) }}
    </template>
    <template v-else>
      <v-progress-circular
        class="mr-2"
        style="width:20px; height:20px;"
        indeterminate
        size="64"
        color="icon-protege"
      />
      {{ $t(`${TRANSLATION_KEY_PREFIX}.requestSummaryDefaultText`) }}
    </template>
  </DocumentsAISummaryMessageFromAI>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'

import { DocumentAISummary } from '@/project/documents/pane/DocumentsAISummaryPane.vue'
import aiSummariesService from '@/services/ai-summaries.service'

import DocumentsAISummaryChatMessage from './DocumentsAISummaryChatMessage.vue'
import DocumentsAISummaryMessageFromAI from './DocumentsAISummaryMessageFromAI.vue'

type DataType = {
  requestedSummary: DocumentAISummary | null
  requestState: QueryState,
  errorCode: string | null,
  errorLimit: number | null,
  TRANSLATION_KEY_PREFIX: string,
}

const TRANSLATION_KEY_PREFIX = 'project.documents.ai-summary.DocumentsAISummaryRequestChatMessage'

export default defineComponent({
  name: 'DocumentsAISummaryRequestChatMessage',
  components: { DocumentsAISummaryMessageFromAI, DocumentsAISummaryChatMessage },
  emits: ['error', 'success'],
  props: {
    documentId: {
      type: Number,
      required: true,
    },
    requestDate: {
      type: String,
      required: true,
    },
  },
  data (): DataType {
    return {
      requestedSummary: null as DataType['requestedSummary'],
      requestState: 'idle',
      errorCode: null,
      errorLimit: null,
      TRANSLATION_KEY_PREFIX,
    }
  },
  async mounted () {
    await this.requestSummary()
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
    shouldDisplayErrorMessage () {
      return this.requestState === 'error'
    },
    isSummaryRequestPending () {
      return this.requestState === 'pending'
    },
    shouldDisplaySummaryMessage () {
      return this.requestState === 'idle' && !!this.requestedSummary
    },
    errorMessage (): string {
      const defaultMessage = this.$t(`${this.TRANSLATION_KEY_PREFIX}.errors.DEFAULT_ERROR_MSG`) as string
      // SUMMARY_*_LIMIT_EXCEEDED come with the configured `limit` to display.
      const limit = this.errorLimit === null ? undefined : this.$n(this.errorLimit, 'decimal')
      return this.$t(`${this.TRANSLATION_KEY_PREFIX}.errors.${this.errorCode}`, { limit }, defaultMessage)
    },
  },
  methods: {
    async requestSummary () {
      this.requestState = 'pending'
      try {
        const { data } = await aiSummariesService.requestDocumentAISummary(
          this.roomMnemo,
          this.documentId,
        )
        this.requestedSummary = data as DocumentAISummary
        this.requestState = 'idle'
        this.errorCode = null
        this.errorLimit = null
        this.$emit('success')
      } catch (error: any) {
        console.error(error)
        this.errorCode = error?.response?.data?.errorCode ?? null
        this.errorLimit = error?.response?.data?.limit ?? null
        this.requestState = 'error'
        this.$emit('error')
      }
    },
  },
})
</script>
